/* eslint-disable react/destructuring-assignment */
import { Typography } from '@mui/material';
import { ContentBlock, RawDraftContentState, RawDraftEntity } from 'draft-js';
import { useRouter } from 'next/router';
import { FC, useEffect, useMemo, useState } from 'react';
import redraft from 'redraft';
import LawLexLink from './LawLexLink';
import Note from '@components/Editor/Note';
import DocumentSearch from '@components/search/DocumentSearch';
import { DocumentSearchText } from '@models/searchable';
import theme from '@styles/theme';
import { LAWLEX } from '@utils/consts/regexp';
import useAmplitude from '@utils/hooks/useAmplitude';
import useReadPosition from '@utils/hooks/useReadPosition';
import { TrackingEventName } from '@utils/types/amplitude/events';
import { NotesMap } from '@utils/types/editors';

const IMAGE_ENTITY_TYPE = 'IMAGE';
export const NOTE_ENTITY_TYPE = 'NOTE';

export const transformKey = (key: string, addToKey = 1): number => parseInt(key, 10) + addToKey;

type GenerateNode = (children: React.ReactNode, { key }: { key: string }) => React.ReactNode;

type GenerateListNode = (
    children: React.ReactNode,
    { depth, keys }: { depth: number; keys: string[] },
) => React.ReactNode;

const BOLD: GenerateNode = (children: React.ReactNode, { key }) => (
    <strong key={`bold-${key}`}>{children}</strong>
);

const ITALIC: GenerateNode = (children: React.ReactNode, { key }) => (
    <em key={`italic-${key}`}>{children}</em>
);

const UNDERLINE: GenerateNode = (children: React.ReactNode, { key }) => (
    <u key={`underline-${key}`}>{children}</u>
);

const SUPERSCRIPT: GenerateNode = (children: React.ReactNode, { key }) => (
    <sup key={`superscript-${key}`}>{children}</sup>
);

const Header: GenerateNode = (children, { key }) => (
    <Typography
        key={`header-${key || JSON.stringify(children)}`}
        data-test-id="header-content"
        style={{ margin: theme.spacing(5, 0, 1) }}
        variant="h3"
    >
        {children}
    </Typography>
);

const Block: GenerateNode = (children: React.ReactNode) =>
    children
        ? (children as React.ReactNode[]).map((child, index) => (
              <Typography
                  key={`block-${index + 1}`}
                  component="div"
                  data-test-id="block-content"
                  style={{ margin: theme.spacing(2, 0) }}
                  variant="body1"
              >
                  {child}
              </Typography>
          ))
        : null;

const OrderedList: GenerateListNode = (children: React.ReactNode, { depth, keys }) =>
    children ? (
        <ol key={`ol-level-${depth}-${keys.join('|')}`}>
            {(children as React.ReactNode[]).map((child, index) => (
                <li key={keys[index]}>{child}</li>
            ))}
        </ol>
    ) : null;

const UnorderedList: GenerateListNode = (children, { depth, keys }) =>
    children ? (
        <ul key={`ul-level-${depth}-${keys.join('|')}`}>
            {(children as React.ReactNode[]).map((child, index) => (
                <li key={keys[index]}>{child}</li>
            ))}
        </ul>
    ) : null;

type NoteWrapper = (
    children: string,
    entity: RawDraftContentState,
    { key }: { key: string },
) => React.ReactNode;

const NoteWrapper: NoteWrapper = (
    _children: string,
    _e: RawDraftContentState,
    { key }: { key: string },
) => {
    const noteIndex = transformKey(key);

    return <Note key={`note-${noteIndex}`} content={_e} noteIndex={noteIndex} />;
};

const NoteWrapperPlusOne: NoteWrapper = (
    _children: string,
    _e: RawDraftContentState,
    { key }: { key: string },
) => {
    const noteIndex = transformKey(key, 2);

    return <Note key={`note-${noteIndex}`} content={_e} noteIndex={noteIndex} />;
};

const baseRenderer = {
    blocks: {
        'header-six': Header,
        'ordered-list-item': OrderedList,
        'unordered-list-item': UnorderedList,
        unstyled: Block,
    },
    inline: {
        BOLD,
        ITALIC,
        SUPERSCRIPT,
        UNDERLINE,
    },
};

interface GetEntitiesProps {
    shiftNotes: boolean;
}

type Entity = { key: string; alt?: string; src?: string };

const getEntities = ({
    shiftNotes,
}: GetEntitiesProps): { entities: Record<string, GenerateNode | NoteWrapper> } => ({
    entities: {
        [IMAGE_ENTITY_TYPE]: (_children: React.ReactNode, entity: Entity): React.ReactNode => (
            <img alt={entity?.alt} src={entity?.src} style={{ maxWidth: '100%' }} />
        ),
        [NOTE_ENTITY_TYPE]: shiftNotes ? NoteWrapperPlusOne : NoteWrapper,
    },
});

// see https://draftjs.org/docs/advanced-topics-decorators/
const lawLexStrategy = (
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void,
): void => {
    const text = contentBlock.getText();
    let matchArr;
    let start;
    // eslint-disable-next-line no-cond-assign
    while ((matchArr = LAWLEX.exec(text)) !== null) {
        start = matchArr.index;
        callback(start, start + matchArr[0].length);
    }
};

const getDecorators = (isNote = false) => ({
    decorators: [
        {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            component: (props: any) => <LawLexLink isNote={isNote} {...props} />,
            strategy: lawLexStrategy,
        },
    ],
});

interface DisplayContentProps {
    shiftNotes?: boolean;
    value: RawDraftContentState;
}

export const DisplayParagraph: FC<DisplayContentProps> = ({ value, shiftNotes = false }) => {
    const { track } = useAmplitude();
    const { scrolledHalfway, scrolledToBottom } = useReadPosition();
    const { pathname } = useRouter();

    const initialContent: DocumentSearchText = useMemo(
        () =>
            redraft(value, {
                ...baseRenderer,
                ...getEntities({ shiftNotes }),
                ...getDecorators(),
            }),
        [value, shiftNotes],
    );
    const [displayedContent, setDisplayedContent] = useState<DocumentSearchText>(initialContent);

    useEffect(() => {
        if (scrolledHalfway) {
            track({
                name: TrackingEventName.DOCUMENT_READ,
                params: {
                    reading_state: 'half',
                    screen_name: pathname,
                },
            });
        }
    }, [track, pathname, scrolledHalfway]);

    useEffect(() => {
        if (scrolledToBottom) {
            track({
                name: TrackingEventName.DOCUMENT_READ,
                params: {
                    reading_state: 'full',
                    screen_name: pathname,
                },
            });
        }
    }, [track, pathname, scrolledToBottom]);

    return (
        <>
            <DocumentSearch
                text={initialContent}
                updateContent={(content) => {
                    setDisplayedContent(content);
                }}
            />
            {displayedContent}
        </>
    );
};

interface DisplayNoteProps {
    value: RawDraftContentState;
    className?: string;
}

export const DisplayNote: FC<DisplayNoteProps> = ({ value }) => {
    return redraft(value, {
        ...baseRenderer,
        ...getDecorators(true),
    });
};

export const prepareNotesMap = (noteMap: { [key: string]: RawDraftEntity }): NotesMap =>
    Object.entries(noteMap).map(([key, { data }]) => ({
        data: data as RawDraftContentState,
        key,
    }));
